import { Metamodel } from '../base/model';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { TriggerProperties } from './trigger-properties';
import { IModel, IViolation } from '../base/annotation';
import { RecurrenceSchedule } from './recurrence-schedule';
import { sanitizeAndFormatString } from '../../shared/utils/string.util';
import moment from 'moment';

export type TriggerFrequency = 'NotSpecified' | 'Minute' | 'Hour' | 'Day' | 'Week' | 'Month';

export const triggerFrequencyToMomentDuration = {
    Minute : 'minutes',
    Hour: 'hours',
    Day: 'days',
    Week: 'weeks',
    Month: 'months',
    NotSpecified: ''
};

@Metamodel.model({
    name: 'ScheduleTrigger',
    display: [shellStringResources.ScheduleLabel, shellStringResources.ModelName_ScheduledTriggers]
})
export class ScheduleTriggerProperties extends TriggerProperties {
    public static readonly MODEL: IModel;

    @Metamodel.attribute({
        display: shellStringResources.StartTime,
        constraint: {
            required: true,
            validator: ScheduleTriggerProperties.validateStartTime
        }
    })
    public startTime: Date;

    @Metamodel.attribute({
        display: shellStringResources.EndTime,
        constraint: {
            required: false,
            validator: ScheduleTriggerProperties.validateEndTime
        }
    })
    public endTime?: Date;

    @Metamodel.attribute({
        display: shellStringResources.Frequency,
        constraint: {
            required: true
        }
    })
    public frequency: TriggerFrequency;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_Interval,
        constraint: {
            required: false,
            whole: true,
            minimum: 1,
            validator: ScheduleTriggerProperties.validateInterval
        }
    })
    public interval?: number;

    @Metamodel.attribute({
        display: shellStringResources.Timezone,
        constraint: {
            required: true
        }
    })
    public timeZone: string;

    @Metamodel.attribute({
        type: RecurrenceSchedule.MODEL,
        display: shellStringResources.ScheduleLabel,
        constraint: {
            required: false,
            validator: ScheduleTriggerProperties.validateRecurrenceSchedule
        }
    })
    public schedule?: RecurrenceSchedule;

    private static validateStartTime(triggerProperties: ScheduleTriggerProperties, startTime: Date): IViolation[] | undefined {
        if (!ScheduleTriggerProperties._isDateValid(startTime)) {
            return [
                {
                    message: () => shellStringResources.Trigger_StartTimeInvalid
                }
            ];
        }
        return undefined;
    }

    private static validateEndTime(triggerProperties: ScheduleTriggerProperties, endTime?: Date): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (endTime && moment(endTime).diff(triggerProperties.startTime) < 0) {
            violations.push({
                message: () => shellStringResources.Trigger_EndTimeAfterStart
            });
        }
        if (endTime && !ScheduleTriggerProperties._isDateValid(endTime)) {
            violations.push({
                message: () => shellStringResources.Trigger_EndTimeInvalid
            });
        }
        return violations.length > 0 ? violations : undefined;
    }

    private static validateRecurrenceSchedule(triggerProperties: ScheduleTriggerProperties, schedule?: RecurrenceSchedule): IViolation[] | undefined {
        if (schedule && schedule.monthlyOccurrences && schedule.monthlyOccurrences.length) {
            const monthlyOccurenceSet = schedule.monthlyOccurrences;
            const allOccurences: Set<string> = new Set();
            for (const occurence of monthlyOccurenceSet) {
                const occurenceString = occurence.day + String(occurence.occurrence);
                if (allOccurences.has(occurenceString)) {
                    return [
                        {
                            message: () => sanitizeAndFormatString(shellStringResources.Trigger_CannotContainDuplicateRecurrenceScheduleOccurence, triggerProperties.named.name)
                        }
                    ];
                }
                allOccurences.add(occurenceString);
            }
        }
        return undefined;
    }

    private static validateInterval(triggerProperties: ScheduleTriggerProperties, interval?: number): IViolation[] | undefined {
        const freq = triggerProperties.frequency;
        if (freq && interval) {
            switch (freq) {
                case 'Month': return interval > 16 ?
                    [{
                        message: () => shellStringResources.Trigger_Interval_Month
                    }]
                    : undefined;
                case 'Week': return interval > 71 ?
                    [{
                        message: () => shellStringResources.Trigger_Interval_Week
                    }]
                    : undefined;
                case 'Day': return interval > 500 ?
                    [{
                        message: () => shellStringResources.Trigger_Interval_Day
                    }]
                    : undefined;
                case 'Hour': return interval > 12000 ?
                    [{
                        message: () => shellStringResources.Trigger_Interval_Hour
                    }]
                    : undefined;
                case 'Minute': return interval > 720000 ?
                    [{
                        message: () => shellStringResources.Trigger_Interval_Minute
                    }]
                    : undefined;
                default:
                    return undefined;
            }
        }
        return undefined;
    }

    private static _isDateValid(date: Date): boolean {
        return !!(date) && !isNaN(date.getMonth()) && !isNaN(date.getDay()) && !isNaN(date.getTime());
    }

    public setRecurrence(frequency: TriggerFrequency, startTime: Date, timeZone: string, interval?: number, endTime?: Date, schedule?: RecurrenceSchedule): void {
        this.frequency = frequency;
        this.interval = interval;
        this.startTime = startTime;
        this.timeZone = timeZone;
        this.endTime = endTime;
        this.schedule = schedule;
    }
}
